import React, { useContext } from 'react';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import { BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import { NavigationContext } from 'buyside/components/presentation/MainSiteLayout/mainSiteLayout';
import ErrorBoundary from 'buyside/components/presentation/ErrorBoundary/errorBoundary';
import Link from 'buyside/components/presentation/Link/link';
import NavigationMessages from 'buyside/components/presentation/MainSiteNavigation/TopNavigation/NavigationMessages/navigationMessages';
import NavigationNotifications from 'buyside/components/presentation/MainSiteNavigation/TopNavigation/NavigationNotifications/navigationNotifications';
import NavigationSearch from 'buyside/components/presentation/MainSiteNavigation/TopNavigation/NavigationSearch/navigationSearch';
import NavigationUser from 'buyside/components/presentation/MainSiteNavigation/TopNavigation/NavigationUser/navigationUser';
import Sparkle from 'buyside/components/presentation/Sparkle/sparkle';
import SubmitResearchButton from 'buyside/components/presentation/Button/SubmitResearchButton/submitResearchButton';

import { buysideSignUpUrl, loginUrl, proHome } from 'buyside/util/urlHelper';
import { useIsMobile } from 'buyside/util/hooks/useIsMobile';
import { useIsHome, useIsHomeWithPage } from 'buyside/util/hooks/useWindowLocation';

import { MainNavigationCurrentUserQuery, Maybe } from 'interfaces/graphql-typings';

import SZLogoIcon from 'buyside/svgs/logo_white.svg';

import styles from './topNavigationStyles.scss';

const TopNavigation: React.FC<{
  currentUser: Maybe<MainNavigationCurrentUserQuery['currentUser']>;
}> = ({ currentUser }) => {
  const isMobile = useIsMobile();
  const homePreRedirect = useIsHome();
  const homePostRedirect = useIsHomeWithPage();

  const { sideNavigationOpen, setSideNavigationOpen } = useContext(NavigationContext) || {};

  const isHome = homePreRedirect || homePostRedirect;

  const showMessages = !currentUser?.isRetail || Boolean(currentUser?.canAccessMessages);

  return (
    <div className={styles.topNavigation} id="main-site-header">
      <button
        className={classNames(styles.iconButton, styles.menuButton, {
          [styles.active]: sideNavigationOpen
        })}
        onClick={(): void => {
          if (setSideNavigationOpen)
            setSideNavigationOpen(prev => {
              localStorage.setItem('sideNavigationOpen', (!prev).toString());
              return !prev;
            });
        }}
        data-testid="toggle-side-navigation"
      >
        <FontAwesomeIcon icon={faBars} />
      </button>
      <div className={styles.topNavigationLogo}>
        <a data-testid="navigation-logo" href={currentUser ? proHome() : '/'}>
          {isMobile ? <SZLogoIcon /> : <img src="/images/logo_darkbg_350.png" />}
        </a>
      </div>
      <div className={styles.topNavigationDropdowns}>
        {currentUser ? (
          <>
            {isHome && currentUser.canWriteIdeas && currentUser.canViewIdeas && (
              <SubmitResearchButton className={styles.writeButton} size={BUTTON_SIZES.TINY} />
            )}
            <NavigationSearch canWriteIdeas={Boolean(currentUser?.canWriteIdeas)} />
            {showMessages && (
              <ErrorBoundary pageName="navigation messages">
                <NavigationMessages
                  unseenMessageCount={currentUser.unseenMessageCount || 0}
                  showUpsell={
                    Boolean(currentUser?.canAccessMessages) && !currentUser?.canManageMessages
                  }
                />
              </ErrorBoundary>
            )}
            <ErrorBoundary pageName="navigation notifications">
              <NavigationNotifications />
            </ErrorBoundary>
            <NavigationUser currentUser={currentUser} />
          </>
        ) : (
          <>
            <Link className={styles.signUpInLink} to={buysideSignUpUrl()}>
              Join SumZero
            </Link>
            <Sparkle>
              <Link className={styles.signUpInLink} to={loginUrl()}>
                Member Sign In
              </Link>
            </Sparkle>
          </>
        )}
      </div>
    </div>
  );
};

export default TopNavigation;
